import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector, useStore, type TypedUseSelectorHook } from 'react-redux'
import type { AppDispatch, AppStore, RootState } from './store'
import { branchesApi } from './api/branches'
import type { Branch } from './api/branches'
import {
  getOntologyBranchHeaderValue,
  getOntologySiteHeaderValue,
  ONTOLOGY_ALL_SITES
} from './api/core'
import { applyBranchSwitch, branchListeners, type BranchListener } from './active-branch'
import { applySiteSwitch, siteListeners, type SiteListener } from './active-site'

export const useAppDispatch: () => AppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
export const useAppStore: () => AppStore = useStore.withTypes<AppStore>()

const BRANCH_STORAGE_KEY = 'ontology-branch'
const SITE_STORAGE_KEY = 'ontology-site'

/**
 * Subscribes to the active ontology branch (the one persisted to
 * localStorage and sent as `X-Ontology-Branch`). Re-renders whenever any
 * caller flips the branch via `useSwitchBranch`, including from another tab.
 */
export function useActiveBranch(): string | undefined {
  const [branch, setBranch] = useState<string | undefined>(getOntologyBranchHeaderValue)

  useEffect(() => {
    const onLocal: BranchListener = (next) => setBranch(next)
    branchListeners.add(onLocal)

    const onStorage = (event: StorageEvent) => {
      if (event.key === BRANCH_STORAGE_KEY) {
        setBranch(getOntologyBranchHeaderValue())
      }
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage)
    }

    return () => {
      branchListeners.delete(onLocal)
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage)
      }
    }
  }, [])

  return branch
}

/**
 * Subscribes to the active ontology site header value. Defaults to `*`, the
 * explicit all-sites sentinel; callers should not treat `undefined` as all-sites
 * because the proxy reserves an absent header for its configured default site.
 */
export function useActiveSite(): string {
  const [site, setSite] = useState<string>(() => getOntologySiteHeaderValue() ?? ONTOLOGY_ALL_SITES)

  useEffect(() => {
    const onLocal: SiteListener = (next) => setSite(next)
    siteListeners.add(onLocal)

    const onStorage = (event: StorageEvent) => {
      if (event.key === SITE_STORAGE_KEY) {
        setSite(getOntologySiteHeaderValue() ?? ONTOLOGY_ALL_SITES)
      }
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage)
    }

    return () => {
      siteListeners.delete(onLocal)
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage)
      }
    }
  }, [])

  return site
}

/**
 * The active branch's full registry row: the row matching the active branch
 * id, or main's row when no branch is active (the baseline). `undefined`
 * while the branch list is loading. The one-stop source for "is the branch
 * I'm editing protected?" — any branch can be protected, not just main.
 */
export function useActiveBranchRecord(): Branch | undefined {
  const activeId = useActiveBranch()
  // Resolve the row by id (any population, any page) rather than scanning the
  // paginated list; the baseline is main, fetched by name. Both are direct
  // lookups, so neither depends on the row being on the list's first page.
  const { data: active } = branchesApi.useGetBranchQuery(activeId ?? '', { skip: !activeId })
  const { data: main } = branchesApi.useGetMainBranchQuery(undefined, { skip: !!activeId })
  return activeId ? active : main
}

/**
 * Switches the active ontology branch with v2's open-then-switch contract:
 * a commit-backed branch's working database is built lazily on first open,
 * so switching to a branch first awaits `openBranch` (one-time ~seconds
 * restore; idempotent and near-instant afterwards), and only on success
 * persists the branch to localStorage, broadcasts to `useActiveBranch`
 * subscribers, and invalidates every branch-scoped RTK Query cache.
 *
 * Open failure leaves the active branch unchanged — the user is never
 * switched onto a database that isn't there — and the error is rethrown for
 * the caller to surface. Passing `undefined` switches to the baseline
 * ("main"), which routes to the default database and needs no open.
 * `isOpening` is true while the one-time restore is in flight.
 */
export function useSwitchBranch(): {
  switchBranch: (branchId: string | undefined) => Promise<void>
  isOpening: boolean
} {
  const dispatch = useAppDispatch()
  const [openBranch, openState] = branchesApi.useOpenBranchMutation()

  const switchBranch = useCallback(
    async (branchId: string | undefined) => {
      if (branchId) {
        await openBranch(branchId).unwrap()
      }
      applyBranchSwitch(dispatch, branchId)
    },
    [dispatch, openBranch]
  )

  return { switchBranch, isOpening: openState.isLoading }
}

export function useSwitchSite(): {
  switchSite: (siteId: string) => void
} {
  const dispatch = useAppDispatch()

  const switchSite = useCallback(
    (siteId: string) => {
      applySiteSwitch(dispatch, siteId || ONTOLOGY_ALL_SITES)
    },
    [dispatch]
  )

  return { switchSite }
}
